import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import QueryState from '@/components/ui/QueryState'
import Skeleton from '@/components/ui/Skeleton'
import { useMe } from '@/features/account/api/queries'
import { useHealth, useShareholders } from '@/features/portfolio/api/queries'
import HealthBody from '@/screens/portfolio/components/health/HealthBody'
import HealthEmpty from '@/screens/portfolio/components/health/HealthEmpty'
import PortfolioFamilyHeader from '@/screens/portfolio/components/PortfolioFamilyHeader'
import { useActiveProfile } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'

export const Route = createFileRoute('/_app/portfolio/health')({ component: HealthScreen })

/** Sized against `HealthHero` + a couple of check cards, per `CardState`'s own rule. */
const HEALTH_SKELETON = (
  <div className="flex flex-col gap-6">
    <Skeleton className="h-48 w-full rounded-lg @lg:h-38" />
    <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2">
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} className="h-36 rounded-lg" />
      ))}
    </div>
  </div>
)

/**
 * Portfolio health (Figma `4236:76175` family: Free, Plus/Sadasya, All
 * steady, Empty) — the page behind Overview's "Open checkup ›" and the
 * sidebar's "Health checkup" row.
 *
 * `GET /api/v5/portfolio/health/` already exists and already backs the
 * Overview teaser (`PortfolioHealthCard`) — `routes/portfolio.ts`'s old "no
 * endpoint" comment was stale (portfolio.md §11). This screen was the one
 * piece actually missing.
 *
 * Route ownership: PF-WP3 owns `routes/portfolio.ts`; its `/portfolio/health`
 * entry (and the `routeTree.gen.ts` regen) is in, so this path is live end to
 * end — the sidebar's "Health checkup" row and Overview's "Open checkup ›"
 * both resolve here.
 *
 * The header is the family's own (switcher + actions), as on the other six
 * tabs: this page is a tab of the section strip now, so the back button and
 * the profile-name subtitle it used to draw are the strip's and the
 * switcher's.
 */
function HealthScreen() {
  const { data } = useShareholders()
  const profiles = useMemo(() => data ?? [], [data])
  const { scopeId } = useActiveProfile(profiles)

  const healthQuery = useHealth(scopeId)
  const { data: me } = useMe()

  return (
    <ScreenLayout
      header={<PortfolioFamilyHeader tab="health" compactAction />}
      pageTitle="Health checkup"
    >
      <QueryState
        query={healthQuery}
        skeleton={HEALTH_SKELETON}
        loadingLabel="Loading your checkup…"
        size="page"
        failed={{ title: 'Portfolio health did not load' }}
      >
        {(report) =>
          // `checks: []` is the "no profile at all" shape (healthReportSchema) —
          // its own onboarding page, not a one-line empty message. An empty
          // but real book answers with six steady checks and renders normally.
          report.checks.length === 0 ? (
            <HealthEmpty />
          ) : (
            <HealthBody report={report} plan={me?.plan} />
          )
        }
      </QueryState>
    </ScreenLayout>
  )
}
